import React from 'react';
import { Alert, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';

interface StepGuideProps {
  mode: 'classic' | 'mass' | 'amplitude' | 'damping' | 'error-investigation';
  currentStep: number;
}

const steps = {
  classic: [
    'Clamp the string firmly at the top of the stand.',
    'Set the pendulum length to the required value and measure to the centre of the bob.',
    'Displace the bob to a small angle, not more than 10°.',
    'Release the bob and allow it to settle into steady motion.',
    'Time 20 complete oscillations at the fiducial marker.',
    'Calculate T = t / 20 and plot T² against L.',
  ],
  mass: [
    'Keep L fixed and vary only the bob mass.',
    'Release from the same small angle each time.',
    'Compare T for each bob mass at the same length.',
  ],
  amplitude: [
    'Use the same length but change the initial angle.',
    'Observe how the period changes as angle increases.',
    'Note the small-angle limit near 15°.',
  ],
  damping: [
    'Set the damping coefficient to a realistic small value.',
    'Record the amplitude reduction over several swings.',
    'Compare the decay to exponential behaviour.',
  ],
  'error-investigation': [
    'Check that the length is measured to the centre of the bob.',
    'Ensure timing is started and stopped at the fiducial marker.',
    'Avoid large angles and clamp slippage.',
  ],
};

const StepGuide: React.FC<StepGuideProps> = ({ mode, currentStep }) => {
  const available = steps[mode] || steps.classic;
  return (
    <Paper sx={{ p: 2, mb: 2, background: '#f8fafc' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Guided practical steps</Typography>
      <List>
        {available.map((step, index) => (
          <ListItem key={`${mode}-${index}`} sx={{ py: 0.5, bgcolor: index === currentStep ? '#e0f2fe' : 'transparent', borderRadius: 1 }}>
            <ListItemText primary={`${index + 1}. ${step}`} secondary={index === currentStep ? 'Current step' : undefined} />
          </ListItem>
        ))}
      </List>
      <Alert severity="info" sx={{ mt: 2 }}>
        For accurate timing, measure from the same fiducial marker and use many oscillations to reduce reaction-time error.
      </Alert>
    </Paper>
  );
};

export default StepGuide;
